import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function AdminShows({ shows, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = async (s) => {
    if (!confirm(`Delete show "${s.title}"? This does NOT delete its episodes.`)) return;
    try {
      await api.delete(`/admin/shows/${s.slug}`);
      toast.success("Show deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-shows-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-neutral-400">{shows.length} shows on the network</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={() => { setEditing(null); setOpen(true); }} className="bg-svn-red text-white font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-red-700 inline-flex items-center gap-2" data-testid="admin-new-show-btn">
              <Plus size={14} /> New Show
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editing ? `Edit · ${editing.title}` : "New Show"}</DialogTitle>
            </DialogHeader>
            <ShowForm show={editing} onSaved={() => { setOpen(false); refresh(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="admin-shows-list">
        {shows.map((s) => (
          <div key={s.id} className="bg-[#0F0F0F] border border-white/10 p-5 flex gap-4">
            <div className="w-20 h-20 shrink-0 bg-black overflow-hidden">
              <img src={s.artwork_url} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold text-white truncate">{s.title}</p>
                  <p className="text-xs text-gold uppercase tracking-widest">{s.language === "es" ? "Español" : "English"} · {s.schedule?.join(", ")}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => { setEditing(s); setOpen(true); }} className="text-neutral-400 hover:text-gold" data-testid={`edit-show-${s.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(s)} className="text-neutral-400 hover:text-svn-red" data-testid={`delete-show-${s.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="mt-2 text-sm text-neutral-400 line-clamp-2 font-body">{s.tagline}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShowForm({ show, onSaved }) {
  const [f, setF] = useState({
    slug: show?.slug || "",
    title: show?.title || "",
    tagline: show?.tagline || "",
    description: show?.description || "",
    topics: (show?.topics || []).join(", "),
    schedule: (show?.schedule || []).join(", "),
    artwork_url: show?.artwork_url || "",
    accent_color: show?.accent_color || "#E50914",
    hosts: (show?.hosts || []).join(", "),
    youtube_url: show?.youtube_url || "",
    spotify_url: show?.spotify_url || "",
    apple_podcasts_url: show?.apple_podcasts_url || "",
    facebook_url: show?.facebook_url || "",
    instagram_url: show?.instagram_url || "",
    tiktok_url: show?.tiktok_url || "",
    language: show?.language || "en",
    order: show?.order ?? 99,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...f,
      topics: f.topics.split(",").map((t) => t.trim()).filter(Boolean),
      schedule: f.schedule.split(",").map((t) => t.trim()).filter(Boolean),
      hosts: f.hosts.split(",").map((t) => t.trim()).filter(Boolean),
      order: Number(f.order) || 0,
    };
    try {
      if (show) {
        await api.put(`/admin/shows/${show.slug}`, payload);
      } else {
        await api.post("/admin/shows", payload);
      }
      toast.success("Show saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const I = (k, label, type = "text") => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input
        type={type}
        value={f[k]}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
        data-testid={`show-form-${k}`}
        className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold"
      />
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-3" data-testid="show-form">
      <div className="grid md:grid-cols-2 gap-3">
        {I("slug", "Slug (URL — lowercase, hyphens)")}
        {I("title", "Title")}
      </div>
      {I("tagline", "Tagline")}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Description</label>
        <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="show-form-description" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {I("topics", "Topics (comma)")}
        {I("schedule", "Schedule (comma — e.g., Mon, Thu)")}
      </div>
      {I("artwork_url", "Artwork URL")}
      <div className="grid md:grid-cols-2 gap-3">
        {I("hosts", "Hosts (comma)")}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Language</label>
          <select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} data-testid="show-form-language" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {I("youtube_url", "YouTube playlist URL")}
        {I("spotify_url", "Spotify show URL")}
        {I("apple_podcasts_url", "Apple Podcasts URL")}
        {I("facebook_url", "Facebook URL")}
        {I("instagram_url", "Instagram URL")}
        {I("tiktok_url", "TikTok URL")}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {I("accent_color", "Accent color (hex)")}
        {I("order", "Display order", "number")}
      </div>
      <button type="submit" disabled={saving} data-testid="show-form-save" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-6 py-3 hover:bg-red-700 disabled:opacity-60">
        {saving ? "Saving…" : "Save Show"}
      </button>
    </form>
  );
}
